import { useCallback } from 'react';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import useUIContext from '~/ui/use-ui-context';
import useRetainDefaultParams from '~/hooks/use-retain-default-params';
import useMainLocation from './use-main-location';

/**
 * Custom hook that transforms a path while maintaining query parameters.
 *
 * This hook combines path transformation with query parameter handling,
 * taking into account UI context (sidebar, modal) states.
 *
 * When a contextual route matches (sidebar, or modal is active),
 * it preserves the context in the transformed path.
 * Otherwise, it returns the path with only the default parameters.
 *
 * @returns A function that accepts a path string and returns a transformed path
 * with appropriate query parameters based on the current UI context.
 * for example:if login on sidebar is open and route is ?sidebar=login and path is /sportsbook
 * ```tsx
 * const transformPathWithQuery = usePathTransformerWithQuery();
 * const transformedPath = transformPathWithQuery('/sportsbook');
 * ```
 * transformedPath will output '/sportsbook?sidebar=login'
 * if path is a contextual path, it will transform the path with the right context
 * if path is /login, transformedPath will be '/?sidebar=login'
 */
const usePathTransformerWithQuery = () => {
	const transformPath = useContextualPathTransformer();
	const { showSidebar, showModal, contexts } = useUIContext();
	const getPathWithDefaultParams = useRetainDefaultParams();
	// Check if any context (sidebar,modal) is mounted
	const isAnyContextMounted = showSidebar || showModal;
	const mainLocation = useMainLocation();

	const transformPathWithQuery = useCallback(
		(path: string) => {
			const pathWithDefaultParams = getPathWithDefaultParams(path);
			// Check if the path matches a contextual route
			const isContextualPath = contexts.find(({ routesMatcher }) => routesMatcher(path));

			// only tansform the path if any context is mounted or if the path is a contextual path
			if (isAnyContextMounted || isContextualPath) {
				return transformPath(path, {
					location: mainLocation,
					preserveContexts: true
				});
			}
			return pathWithDefaultParams;
		},
		[transformPath, getPathWithDefaultParams, isAnyContextMounted, contexts, mainLocation]
	);

	return transformPathWithQuery;
};

export default usePathTransformerWithQuery;
